import React, { useEffect, useState } from 'react';
import { makeRequest } from "../../utils/httpRequest";
import { useRecoilState } from "recoil";
import { loadingAtom } from "../../atoms";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../core/PermissionContext";
import { UserPermission, formatDateToAmericanFormat } from "../../utils/utils";
import Loading from "../../core/Loading";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import StatusSelect from "./StatusSelect";
import WhatsAppButton from '../../core/WhatsAppButton';
import { showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const AfterSaleManager = () => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const { hasUserPermission, loadingPermissions } = usePermission();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    const newEntity = {
        id: 0,
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        saleDate: null,
        afterSaleDate: null,
        type: 'OTHER',
        status: 'PENDING',
        productName: '',
        attendant: '',
        description: '',
        priority: 0,
        notes: ''
    };

    const [formData, setFormData] = useState(newEntity);
    const [repeatMonthlyEnabled, setRepeatMonthlyEnabled] = useState(false);
    const [repeatCount, setRepeatCount] = useState(1);
    const [accessibleCompanies, setAccessibleCompanies] = useState([]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'number' ? parseInt(value) : value
        }));
    };

    const handleDateChange = (name, date) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: date
        }));
    };

    async function fetchAccessibleCompanies() {
        try {
            const response = await makeRequest(apiUrl + '/api/aftersales/accessible-companies', 'GET');
            if (Array.isArray(response)) {
                setAccessibleCompanies(response);
            } else {
                setAccessibleCompanies([]);
            }
        } catch (error) {
            console.error('Error fetching accessible companies:', error);
            setAccessibleCompanies([]);
        }
    }

    async function makeQuery() {
        if (id) {
            try {
                setLoading(true);
                const url = apiUrl + '/api/aftersales/id=' + id;
                const response = await makeRequest(url, 'GET');
                
                // Convert date strings to Date objects for DatePicker
                if (response.saleDate) {
                    response.saleDate = new Date(response.saleDate + 'T00:00:00');
                }
                if (response.afterSaleDate) {
                    response.afterSaleDate = new Date(response.afterSaleDate + 'T00:00:00');
                }
                
                setFormData(response);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setFormData(newEntity);
            setRepeatMonthlyEnabled(false);
            setRepeatCount(1);
        }
    }

    useEffect(() => {
        fetchAccessibleCompanies();
        makeQuery();
    }, [id]);

    useEffect(() => {
        if (!loadingPermissions) {
            if (id && !hasUserPermission(UserPermission.EDIT_AFTER_SALE)) {
                navigate('/unauthorizedaccess');
            } else if (!id && !hasUserPermission(UserPermission.CREATE_AFTER_SALE)) {
                navigate('/unauthorizedaccess');
            }
        }
    }, [loadingPermissions, hasUserPermission, id, navigate]);

    const onConfirm = async () => {
        try {
            if (validateInputs()) {
                setLoading(true);
                await saveEntity();
                navigate('/app/aftersalelist');
            }
        } catch (error) {
            console.error('Error saving:', error);
            showAlert('Erro ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    function validateInputs() {
        if (!formData.customerName || formData.customerName === '') {
            showAlert('Preencha o campo nome do cliente.');
            return false;
        }
        if (!formData.status || formData.status === '') {
            showAlert('Selecione o status.');
            return false;
        }
        if (!id && repeatMonthlyEnabled && (!formData.afterSaleDate || (formData.afterSaleDate instanceof Date && isNaN(formData.afterSaleDate.getTime())))) {
            showAlert('Para repetir mensalmente, informe a Data do Pós-Venda.');
            return false;
        }
        return true;
    }

    async function saveEntity() {
        const url = apiUrl + '/api/aftersales';
        setLoading(true);
        
        try {
            // Convert Date objects to string format for API
            const payload = { ...formData };
            if (payload.saleDate instanceof Date) {
                payload.saleDate = formatDateToAmericanFormat(payload.saleDate);
            }
            if (payload.afterSaleDate instanceof Date) {
                payload.afterSaleDate = formatDateToAmericanFormat(payload.afterSaleDate);
            }
            if (!id) {
                payload.repeatMonthly = repeatMonthlyEnabled;
                payload.repeatCount = repeatCount;
            }

            await makeRequest(url, id ? 'PUT' : 'POST', {
                'Content-Type': 'application/json',
            }, JSON.stringify(payload));
        } catch (error) {
            console.log(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    if (loadingPermissions) {
        return <Loading />;
    }

    return (
        <div className="app-form-page">
            <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Pós-Venda</div>
            {accessibleCompanies.length > 1 && (
                <div style={{ marginTop: '5px', fontStyle: 'italic', color: '#555' }}>
                    Empresa: {(() => {
                        const company = accessibleCompanies.find(company => company.id === formData.companyId) || accessibleCompanies[0];
                        if (!company) {
                            return '-';
                        }
                        const label = company.fantasyName && company.fantasyName.trim() !== '' ? company.fantasyName : company.name;
                        return label || '-';
                    })()}
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}>
                    <div>Nome do Cliente *</div>
                    <input 
                        type="text" 
                        style={{ width: '100%' }} 
                        value={formData.customerName} 
                        name="customerName"
                        onChange={handleChange}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div>Telefone</div>
                    <div className="phone-field-inline">
                        <input
                            type="text"
                            style={{ width: '100%' }}
                            value={formData.customerPhone}
                            name="customerPhone"
                            onChange={handleChange}
                        />
                        <WhatsAppButton phoneNumber={formData.customerPhone} personName={formData.customerName}/>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}>
                    <div>Email</div>
                    <input 
                        type="email" 
                        style={{ width: '100%' }} 
                        value={formData.customerEmail} 
                        name="customerEmail"
                        onChange={handleChange}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div>Data da Venda</div>
                    <DatePicker
                        className="datepicker"
                        selected={formData.saleDate}
                        onChange={(date) => handleDateChange('saleDate', date)}
                        dateFormat="dd/MM/yyyy"
                        isClearable
                        autoComplete="off"
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div>Data do Pós-Venda</div>
                    <DatePicker
                        className="datepicker"
                        selected={formData.afterSaleDate}
                        onChange={(date) => handleDateChange('afterSaleDate', date)}
                        dateFormat="dd/MM/yyyy"
                        isClearable
                        autoComplete="off"
                    />
                </div>
            </div>

            {!id && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={repeatMonthlyEnabled}
                            onChange={(e) => setRepeatMonthlyEnabled(e.target.checked)}
                        />
                        Repetir mensalmente
                    </label>
                    {repeatMonthlyEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <select
                                value={repeatCount}
                                onChange={(e) => setRepeatCount(parseInt(e.target.value, 10))}
                                style={{ minWidth: '60px' }}
                            >
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            <span>vezes</span>
                        </div>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}>
                    <div>Produto/Serviço</div>
                    <input 
                        type="text" 
                        style={{ width: '100%' }} 
                        value={formData.productName} 
                        name="productName"
                        onChange={handleChange}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div>Atendente</div>
                    <input
                        type="text"
                        style={{ width: '100%' }}
                        value={formData.attendant}
                        name="attendant"
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}>
                    <div>Status *</div>
                    <div style={{ marginTop: '3px' }}>
                        <StatusSelect
                            value={formData.status}
                            onChange={(newStatus) => setFormData((prevData) => ({ ...prevData, status: newStatus }))}
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '10px' }}>
                <div>Descrição</div>
                <textarea 
                    style={{ width: '100%', minHeight: '100px' }} 
                    value={formData.description} 
                    name="description"
                    onChange={handleChange}
                />
            </div>

            <div style={{ marginTop: '10px' }}>
                <div>Observações</div>
                <textarea 
                    style={{ width: '100%', minHeight: '80px' }} 
                    value={formData.notes} 
                    name="notes"
                    onChange={handleChange}
                />
            </div>

            <ActionButtonsManagerView>
                <ConfirmButton onClick={onConfirm} />
            </ActionButtonsManagerView>
        </div>
    );
};

export default AfterSaleManager;
